
import { Button } from "@mui/material";
import "./choose-language.page.scss";
import FLAG_ENG from "../assets/images/flag-eng.png";
import FLAG_TH from "../assets/images/flag-th.png";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck, faRotateRight, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../providers/language.provider";
import { useNavigate, useParams } from "react-router-dom";
import LOGO_SO from "../assets/images/logo-so-black.png";
import { trackEvent } from "../utils/trackGA4.util";
import UtilityService from "../utils/ga4.util";
import { GAConfig } from "../utils/ga-config.util";
import { getByPassChooseLanguage, trackInviteEmailLinkOpen } from "../services/interview-api.services";
import APIService from "../services/api.services";
import Lottie from "lottie-react";
import LOADING_BLUE from "../assets/json/loading-blue.json";
import LOADING_GRADIENT from "../assets/json/loading-gradient.json";

const VOICE_CONFIG = [
    { value: "M1", name: "Alex",   gender: "male",   bg: "linear-gradient(135deg,#667eea,#764ba2)" },
    { value: "M2", name: "Marcus", gender: "male",   bg: "linear-gradient(135deg,#4facfe,#00f2fe)" },
    { value: "F1", name: "Aria",   gender: "female", bg: "linear-gradient(135deg,#f093fb,#f5576c)" },
    { value: "F2", name: "Emma",   gender: "female", bg: "linear-gradient(135deg,#fccb90,#d57eeb)" },
] as const;

const INACTIVE_WIDTH = 130; // px — slot width for non-active voices
const ACTIVE_WIDTH   = 200; // px — slot width for active voice

export const TTS_SPEED_KEY = "interview_tts_speed";
const SPEED_OPTIONS = ["X1", "X2", "X3", "X4"] as const;

const ChooseLanguage = () => {
    const { id, candidate_id } = useParams<{ id: string; candidate_id: string; email: string }>();
    const { translations, switchLanguage } = useLanguage();
    const [isTh, setIsTh] = useState(id !== '9b13f125-8003-40b3-8b4d-a81c390fef7f');
    const [isEn, setIsEn] = useState(id === '9b13f125-8003-40b3-8b4d-a81c390fef7f');
    const [isAutoNavigating, setIsAutoNavigating] = useState(false);
    const [isPlayingSample, setIsPlayingSample] = useState(false);
    const [voiceDisplayIndex, setVoiceDisplayIndex] = useState<number>(() => {
        const saved = localStorage.getItem('interview_voice_selection');
        const idx = VOICE_CONFIG.findIndex(v => v.value === saved);
        return idx >= 0 ? idx : 0;
    });
    const [speedSelection, setSpeedSelection] = useState<string>(() => {
        const saved = localStorage.getItem(TTS_SPEED_KEY);
        return SPEED_OPTIONS.includes(saved as any) ? saved! : "X1";
    });
    const hasCheckedRef = useRef(false);
    const inviteLinkTrackedRef = useRef(false);
    const previewAudioRef = useRef<HTMLAudioElement | null>(null);
    const carouselWrapRef = useRef<HTMLDivElement | null>(null);
    const [carouselWidth, setCarouselWidth] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (!id || id === '9b13f125-8003-40b3-8b4d-a81c390fef7f') {
            switchLanguage("en");
            return;
        }
        switchLanguage("th");
    }, [id, switchLanguage]);

    useEffect(() => {
        if (!id || !candidate_id || inviteLinkTrackedRef.current) return;
        inviteLinkTrackedRef.current = true;
        trackInviteEmailLinkOpen({ job_question_id: id, candidate_id });
    }, [id, candidate_id]);

    useEffect(() => {
        if (!id || hasCheckedRef.current) return;
        const checkByPassLanguage = async () => {
            try {
                const response = await getByPassChooseLanguage(id);
                if (response.is_success && response.data) {
                    const { en = [], th = [] } = response.data;
                    if (en.includes(id)) {
                        hasCheckedRef.current = true;
                        setIsAutoNavigating(true);
                        setIsEn(true); setIsTh(false);
                        switchLanguage("en");
                        trackEvent(GAConfig.action.verifyRoom, UtilityService());
                        navigate("/" + id + "/" + candidate_id + "/verify/");
                        return;
                    }
                    if (th.includes(id)) {
                        hasCheckedRef.current = true;
                        setIsAutoNavigating(true);
                        setIsTh(true); setIsEn(false);
                        switchLanguage("th");
                        trackEvent(GAConfig.action.verifyRoom, UtilityService());
                        navigate("/" + id + "/" + candidate_id + "/verify/");
                        return;
                    }
                }
            } catch (e) {
                console.error("Error checking by-pass-choose-language:", e);
            } finally {
                hasCheckedRef.current = true;
            }
        };
        checkByPassLanguage();
    }, [id, candidate_id, navigate, switchLanguage]);

    useEffect(() => {
        const syncCarouselWidth = () => {
            setCarouselWidth(carouselWrapRef.current?.clientWidth || 0);
        };
        syncCarouselWidth();
        window.addEventListener("resize", syncCarouselWidth);
        return () => window.removeEventListener("resize", syncCarouselWidth);
    }, []);

    const handleClickLang = (field: string) => {
        if (isAutoNavigating || id === '9b13f125-8003-40b3-8b4d-a81c390fef7f') return;
        if (field === "en") { setIsTh(false); setIsEn(true); switchLanguage("en"); }
        else { setIsTh(true); setIsEn(false); switchLanguage("th"); }
        trackEvent(GAConfig.action.changeLanguage, UtilityService());
    };

    const stopPreviewAudio = () => {
        if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
        setIsPlayingSample(false);
    };

    const handleSelectVoice = (idx: number) => {
        stopPreviewAudio();
        setVoiceDisplayIndex(idx);
    };

    const handleNavigateVoice = (dir: number) => {
        const next = (voiceDisplayIndex + dir + VOICE_CONFIG.length) % VOICE_CONFIG.length;
        handleSelectVoice(next);
    };

    const currentLang = isTh ? "th" : "en";

    // Auto-play sample whenever voice/speed/language sample text changes
    useEffect(() => {
        let cancelled = false;
        const cv = VOICE_CONFIG[voiceDisplayIndex];
        const sampleText = cv.gender === "male"
            ? (translations.voice_sample_text_male || "Hello, I'll be your interviewer today.")
            : (translations.voice_sample_text_female || "Hello, I'll be your interviewer today.");

        const play = async () => {
            setIsPlayingSample(true);
            try {
                const res = await APIService().getTTS({
                    input: sampleText,
                    voice: cv.value,
                    speed: speedSelection,
                    lang: currentLang,
                });
                if (cancelled) { setIsPlayingSample(false); return; }
                if (res?.data) {
                    const contentType = res.headers?.["content-type"] || "audio/wav";
                    const blob = new Blob([new Uint8Array(res.data)], { type: contentType });
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    previewAudioRef.current = audio;
                    audio.onended = () => { setIsPlayingSample(false); URL.revokeObjectURL(url); previewAudioRef.current = null; };
                    audio.onerror = () => { setIsPlayingSample(false); URL.revokeObjectURL(url); previewAudioRef.current = null; };
                    await audio.play();
                } else { setIsPlayingSample(false); }
            } catch (err) {
                if (!cancelled) { console.error("Error playing voice sample:", err); setIsPlayingSample(false); }
            }
        };

        play();
        return () => { cancelled = true; stopPreviewAudio(); };
    }, [voiceDisplayIndex, speedSelection, currentLang, translations.voice_sample_text_male, translations.voice_sample_text_female]);

    const handlePlaySample = async () => {
        if (isPlayingSample) return;
        const cv = VOICE_CONFIG[voiceDisplayIndex];
        const sampleText = cv.gender === "male"
            ? (translations.voice_sample_text_male || "Hello, I'll be your interviewer today.")
            : (translations.voice_sample_text_female || "Hello, I'll be your interviewer today.");
        setIsPlayingSample(true);
        try {
            stopPreviewAudio();
            const res = await APIService().getTTS({
                input: sampleText,
                voice: cv.value,
                speed: speedSelection,
                lang: currentLang,
            });
            if (res?.data) {
                const contentType = res.headers?.["content-type"] || "audio/wav";
                const blob = new Blob([new Uint8Array(res.data)], { type: contentType });
                const url = URL.createObjectURL(blob);
                const audio = new Audio(url);
                previewAudioRef.current = audio;
                audio.onended = () => { setIsPlayingSample(false); URL.revokeObjectURL(url); previewAudioRef.current = null; };
                audio.onerror = () => { setIsPlayingSample(false); URL.revokeObjectURL(url); previewAudioRef.current = null; };
                await audio.play();
            } else { setIsPlayingSample(false); }
        } catch (err) {
            console.error("Error playing voice sample:", err);
            setIsPlayingSample(false);
        }
    };

    const submit = () => {
        if (id === '9b13f125-8003-40b3-8b4d-a81c390fef7f') {
            trackEvent(GAConfig.action.verifyRoom, UtilityService());
            navigate("/" + id + "/" + candidate_id + "/verify/");
            return;
        }
        if (isAutoNavigating || (!isEn && !isTh)) return;
        localStorage.setItem('interview_voice_selection', VOICE_CONFIG[voiceDisplayIndex].value);
        localStorage.setItem(TTS_SPEED_KEY, speedSelection);
        stopPreviewAudio();
        trackEvent(GAConfig.action.verifyRoom, UtilityService());
        navigate("/" + id + "/" + candidate_id + "/verify/");
    };

    const cv = VOICE_CONFIG[voiceDisplayIndex];
    const lottieData = cv.gender === 'male' ? LOADING_BLUE : LOADING_GRADIENT;
    const voiceGenderLabel = cv.gender === 'male'
        ? (translations.voice_gender_male || 'Male')
        : (translations.voice_gender_female || 'Female');

    // Center active item using real viewport width (px), not transform percentages.
    const activeCenterFromTrackStart = voiceDisplayIndex * INACTIVE_WIDTH + ACTIVE_WIDTH / 2;
    const trackOffset = (carouselWidth / 2) - activeCenterFromTrackStart;

    return (
        <div className="cl-page">
            {/* Header */}
            <div className="cl-header">
                <img src={LOGO_SO} className="cl-logo" alt="logo" />
            </div>

            {/* Scrollable content — vertically centered block */}
            <div className="cl-content">
                <div className="cl-content-spacer" aria-hidden="true" />
                <div className="cl-content-inner">
            {/* ── Step 1: Language ─────────────────────────────────── */}
            <div className="cl-step-header">
                <span className="cl-step-badge">1</span>
                <span className="cl-step-title">{translations.choose_language}</span>
            </div>
            <div className="cl-lang-cards">
                <div
                    className={`cl-lang-card${isEn ? ' active' : ''}`}
                    onClick={() => handleClickLang("en")}
                    role="button"
                    aria-pressed={isEn}
                >
                    <img src={FLAG_ENG} alt="EN flag" className="cl-lang-flag" />
                    <span className="cl-lang-name">English</span>
                    {isEn && <FontAwesomeIcon icon={faCheck} className="cl-lang-check" />}
                </div>
                <div
                    className={`cl-lang-card${isTh ? ' active' : ''}`}
                    onClick={() => handleClickLang("th")}
                    role="button"
                    aria-pressed={isTh}
                >
                    <img src={FLAG_TH} alt="TH flag" className="cl-lang-flag" />
                    <span className="cl-lang-name">ภาษาไทย</span>
                    {isTh && <FontAwesomeIcon icon={faCheck} className="cl-lang-check" />}
                </div>
            </div>

            {/* ── Divider ───────────────────────────────────────────── */}
            <div className="cl-section-divider" />

            {/* ── Step 2: Voice ─────────────────────────────────────── */}
            <div className="cl-step-header">
                <span className="cl-step-badge secondary">2</span>
                <span className="cl-step-title">{translations.voice_modal_title}</span>
            </div>

            {/* Voice Carousel */}
            <div className="cl-carousel-wrap" ref={carouselWrapRef}>
                <button className="cl-carousel-arrow left" onClick={() => handleNavigateVoice(-1)} aria-label="previous voice">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className="cl-carousel-track" style={{ transform: `translateX(${trackOffset}px)` }}>
                    {VOICE_CONFIG.map((v, i) => {
                        const isActive = i === voiceDisplayIndex;
                        return (
                            <div
                                key={v.value}
                                className={`cl-carousel-item${isActive ? ' active' : ''}`}
                                onClick={() => handleSelectVoice(i)}
                            >
                                {isActive ? (
                                    /* Active: full concentric rings + lottie */
                                    <div className="cl-ring-outer">
                                        <div className="cl-ring-middle">
                                            <div className="cl-ring-inner">
                                                <Lottie animationData={lottieData} loop className="cl-lottie" />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Inactive: single coloured circle */
                                    <div className="cl-inactive-circle" style={{ background: v.bg }}>
                                        <span className="cl-inactive-initial">{v.name.charAt(0)}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <button className="cl-carousel-arrow right" onClick={() => handleNavigateVoice(1)} aria-label="next voice">
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>

            {/* Voice name + playing indicator */}
            <div className="cl-voice-info">
                <div className="cl-voice-name-row">
                    <span className="cl-voice-name">{cv.name}</span>
                    {isPlayingSample
                        ? <span className="cl-voice-playing-dot" />
                        : (
                            <button className="cl-replay-btn" onClick={handlePlaySample} aria-label="replay">
                                <FontAwesomeIcon icon={faRotateRight} />
                            </button>
                        )
                    }
                </div>
                {/* <span className="cl-voice-desc">{voiceDesc}</span> */}
            </div>

            {/* Stats row — Voice index + Gender only */}
            <div className="cl-stats-row">
                <div className="cl-stat">
                    <span className="cl-stat-label">{translations.voice_stat_voice || 'Voice'}</span>
                    <span className="cl-stat-value">{voiceDisplayIndex + 1} / {VOICE_CONFIG.length}</span>
                </div>
                <div className="cl-stat-divider" />
                <div className="cl-stat">
                    <span className="cl-stat-label">{translations.voice_stat_gender || 'Gender'}</span>
                    <span className="cl-stat-value" data-gender={cv.gender}>{voiceGenderLabel}</span>
                </div>
            </div>

            {/* Speed: X1 = normal, X2 = เร็ว, X3 = เร็วมาก, X4 = เร็วที่สุด */}
            <p className="cl-section-label" style={{ marginTop: '12px' }}>{translations.voice_speed_label}</p>
            <div className="cl-speed-pills">
                {SPEED_OPTIONS.map((speed) => (
                    <button
                        key={speed}
                        type="button"
                        className={`cl-speed-pill${speedSelection === speed ? ' active' : ''}`}
                        onClick={() => setSpeedSelection(speed)}
                    >
                        {translations[`voice_speed_${speed.toLowerCase()}`] || speed}
                    </button>
                ))}
            </div>

                </div>
                <div className="cl-content-spacer" aria-hidden="true" />
            </div>

            {/* Continue — fixed bottom */}
            <Button className="cl-submit-btn" onClick={submit}>
                {translations.next_button}
                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
            </Button>
        </div>
    );
};

export default ChooseLanguage;
